/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo } from 'react';
import { isEmpty, isNull, isUndefined } from 'lodash';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ERROR_403 } from 'common/config/api';
import { forbiddenPath, notFoundPath } from 'common/config/urls';
import {
  FIRM_SETTINGS_SLUG,
  INTELLIGENCE_SLUG,
  SCIM_CONFIGURATION_TAB,
  SINGLE_SIGN_ON_TAB,
} from 'common/constants/firms';
import { BULK_ACTIONS, EXCEL_EXPORT, PDF_EXPORT } from 'common/constants/pageActions';
import { FIRM_TYPE } from 'common/constants/pageTypes';
import { FIRM_WATERFALL, USER_MANAGEMENT_TAB } from 'common/constants/user';
import { useExtraPageActionVisibility } from 'common/hooks';
import { useStore } from 'common/store';
import { Firm } from 'common/types/firm';
import { MenuAndSubMenu, UseStoreValues } from 'common/types/store';
import { PdfExportConfigDialog } from 'components';
import useCheckFeatures from 'components/FeatureEnabler/hooks';
import { useLayoutContext } from 'context/LayoutContext';
import { UserPermissions as UserPermissionsView } from 'pages';
import FirmWaterfall from 'pages/Firms/FirmWaterfall';
import { useUserUtils } from 'utilities/userUtils';
import { FIRM_NAVIGATION_ROUTES, type FirmNavigationRouteBuilder } from './firmNavigation';
import { RouteParams } from './Firms.props';
import Settings from './FirmSettings';
import { ScimConfiguration, SingleSignOn } from './pages';

const Firms = () => {
  const {firmSlugParam, tableSlugParam} = useParams<RouteParams>();
  const navigate = useNavigate();
  const [{firmList, user}] = useStore() as unknown as UseStoreValues;
  const [features, checkFeature] = useCheckFeatures();

  const {
    setNavItems,
    setPageTitle,
    setPageType,
    setPageBreadcrumbs,
    toggleExportDialog,
    toggleBulkActionsDialog,
    togglePdfDialog,
    openPdfDialog,
    updateExtraPageActions,
    viewOnlyUser,
  } = useLayoutContext();

  const {isStaffUser, isSuperUser} = useUserUtils();
  const actionVisibility = useExtraPageActionVisibility({
    pdfExport: isStaffUser() || isSuperUser(),
  });

  const activeFirm = useMemo(
    () =>
      isEmpty(firmList) || (firmList as unknown as string) === ERROR_403 || !firmSlugParam
        ? null
        : (firmList as Firm[]).find(item => item.slug === firmSlugParam),
    [firmList, firmSlugParam]
  );

  const navigateToUrl = (getUrl: FirmNavigationRouteBuilder) => {
    if (firmSlugParam) {
      navigate(getUrl(firmSlugParam));
    }
  };

  const menuItems: MenuAndSubMenu[] = useMemo(() => {
    const filteredRoutes = FIRM_NAVIGATION_ROUTES.filter(route => {
      if (route.featureFlag && !checkFeature(route.featureFlag)) {
        return false;
      }
      return true;
    });

    if (viewOnlyUser) {
      return filteredRoutes
        .filter(route => route.id === 'summary')
        .map(route => ({
          id: route.id,
          slug: route.slug,
          title: route.title,
          itemKey: route.itemKey,
          callback: () => navigateToUrl(route.route),
        }));
    }

    return filteredRoutes.map(route => ({
      id: route.id,
      slug: route.slug,
      title: route.title,
      itemKey: route.itemKey,
      callback: () => navigateToUrl(route.route),
    }));
  }, [firmSlugParam, features, viewOnlyUser]);

  useEffect(() => {
    if (!isEmpty(activeFirm)) {
      if (activeFirm.name) {
        setPageTitle?.(activeFirm.name);
      }
    }

    return () => {
      setPageTitle?.(null);
      setPageType?.(FIRM_TYPE);
    };
  }, [activeFirm]);

  useEffect(() => {
    const extraActions = [];

    if (actionVisibility.excelExport) {
      extraActions.push({
        label: EXCEL_EXPORT,
        callback: toggleExportDialog,
        isActive: true,
      });
    }
    if (actionVisibility.bulkActions) {
      extraActions.push({
        label: BULK_ACTIONS,
        callback: toggleBulkActionsDialog,
        isActive: true,
      });
    }
    if (actionVisibility.pdfExport) {
      extraActions.push({
        label: PDF_EXPORT,
        callback: togglePdfDialog,
        isActive: true,
      });
    }

    updateExtraPageActions?.(extraActions);
  }, [
    updateExtraPageActions,
    toggleBulkActionsDialog,
    togglePdfDialog,
    toggleExportDialog,
    actionVisibility.excelExport,
    actionVisibility.bulkActions,
    actionVisibility.pdfExport,
  ]);

  useEffect(() => {
    setNavItems?.(menuItems);
    return () => setNavItems?.(null);
  }, [menuItems]);

  const formatSlug = (slug = ''): string => {
    const slugArray = slug.split('-');
    return slugArray.map(item => item.charAt(0).toUpperCase() + item.slice(1)).join(' ');
  };

  const breadcrumbs = useMemo(
    () => [
      {
        title: activeFirm?.name ?? 'Admin',
      },
      {
        title: formatSlug(tableSlugParam),
      },
    ],
    [activeFirm, tableSlugParam]
  );

  useEffect(() => {
    setPageBreadcrumbs?.(breadcrumbs);
  }, [activeFirm, tableSlugParam]);

  if (activeFirm && isEmpty(activeFirm)) {
    navigate(notFoundPath);
    return null;
  }

  if (isUndefined(activeFirm) && !isNull(user)) {
    navigate(forbiddenPath);
    return null;
  }

  const renderCurrentView = () => {
    switch (tableSlugParam) {
      case INTELLIGENCE_SLUG:
        return null;
      case FIRM_SETTINGS_SLUG:
        return <Settings/>;
      case FIRM_WATERFALL:
        return <FirmWaterfall/>;
      case USER_MANAGEMENT_TAB:
        return <UserPermissionsView/>;
      case SINGLE_SIGN_ON_TAB:
        return <SingleSignOn/>;
      case SCIM_CONFIGURATION_TAB:
        return <ScimConfiguration/>;
      default:
        return <Navigate to={notFoundPath} replace/>;
    }
  };

  return (
    <>
      {renderCurrentView()}
      <PdfExportConfigDialog open={openPdfDialog} onClose={togglePdfDialog}/>
    </>
  );
};

export default Firms;
